import { useEffect, useState, type DragEvent } from 'react'
import type { AttachmentKind, AttachmentResponse } from '@/api/types'
import { Icon } from '@/components/Icon'
import { SegmentedControl } from '@/components/SegmentedControl'
import { platform } from '@/platform'
import { formatVisitDate } from '@/lib/dates'
import { cx } from '@/lib/cx'
import { ACCEPTED_TYPES, useUploader } from './useUploader'

export interface UploaderHandle {
  /** Sends anything captured before the visit existed. */
  flushQueued: (visitId: string) => Promise<void>
}

/** Attaching is non-blocking: the doctor keeps typing while a file uploads, and
 *  a file chosen before the consultation exists waits for the save. */
const KINDS: { value: AttachmentKind; label: string }[] = [
  { value: 'xray', label: 'X-ray' },
  { value: 'scan', label: 'Scan' },
  { value: 'report', label: 'Report' },
  { value: 'other', label: 'Other' },
]

export function AttachmentUploader({
  visitId,
  existing = [],
  kind: fixedKind,
  onReady,
}: {
  visitId: string | null
  /** Already-saved documents, shown alongside what is being added. */
  existing?: AttachmentResponse[]
  kind?: AttachmentKind
  onReady?: (handle: UploaderHandle) => void
}) {
  const { uploads, upload, remove, flushQueued } = useUploader(visitId)
  const [dragging, setDragging] = useState(false)
  // The brief names scanning and X-ray documents specifically, and the record
  // stores the distinction — but nothing in the UI ever asked, so every file a
  // doctor attached was filed as a "report".
  const [chosenKind, setChosenKind] = useState<AttachmentKind>('report')
  const kind: AttachmentKind = fixedKind ?? chosenKind

  useEffect(() => {
    onReady?.({ flushQueued })
  }, [onReady, flushQueued])

  async function fromPicker() {
    const files = await platform.camera.pickFiles({ accept: ACCEPTED_TYPES, multiple: true })
    for (const file of files) void upload(file.blob, file.filename, file.contentType, kind)
  }

  function onDrop(event: DragEvent) {
    event.preventDefault()
    setDragging(false)
    for (const file of Array.from(event.dataTransfer.files)) {
      void upload(file, file.name, file.type || 'application/octet-stream', kind)
    }
  }

  return (
    <div
      onDragOver={(event) => {
        event.preventDefault()
        setDragging(true)
      }}
      onDragLeave={() => setDragging(false)}
      onDrop={onDrop}
      onPaste={(event) => {
        // A screenshot of a lab portal pasted straight in is a real workflow.
        for (const item of Array.from(event.clipboardData.files)) {
          void upload(item, item.name || 'pasted-image.png', item.type, kind)
        }
      }}
    >
      {!fixedKind && (
        <div className="mb-3">
          <SegmentedControl
            label="Document type"
            options={KINDS}
            value={chosenKind}
            onChange={setChosenKind}
          />
        </div>
      )}

      <div className="grid grid-cols-[repeat(auto-fill,minmax(7rem,1fr))] gap-4">
        {existing.map((document) => (
          <div className="flex min-w-0 flex-col gap-2" key={document.id}>
            <span className="relative grid aspect-[1/0.95] w-full place-items-center overflow-hidden rounded-md border border-line-soft bg-subtle p-0 text-fg-subtle">
              <Icon
                name={document.content_type === 'application/pdf' ? 'file' : 'image'}
                size="1.75rem"
              />
            </span>
            <span className="truncate text-xs font-medium text-fg" title={document.original_filename}>
              {document.original_filename}
            </span>
            {document.uploaded_at && (
              <span className="text-xs text-fg-subtle">
                {formatVisitDate(document.uploaded_at.slice(0, 10))}
              </span>
            )}
          </div>
        ))}

        {uploads.map((item) => (
          <div className="flex min-w-0 flex-col gap-2" key={item.id}>
            <button
              type="button"
              className="relative grid aspect-[1/0.95] w-full place-items-center overflow-hidden rounded-md border border-line-soft bg-subtle p-0 text-fg-subtle"
              aria-label={`Remove ${item.filename}`}
              onClick={() => remove(item.id)}
            >
              {item.previewUrl ? (
                <img className="size-full object-cover" src={item.previewUrl} alt={item.filename} />
              ) : (
                <Icon name="file" size="1.75rem" />
              )}
            </button>
            <span className="truncate text-xs font-medium text-fg" title={item.filename}>
              {item.filename}
            </span>
            {item.status === 'uploading' ? (
              <span
                className="h-1 w-full overflow-hidden rounded-full bg-subtle"
                role="progressbar"
                aria-valuenow={Math.round(item.progress * 100)}
                aria-valuemin={0}
                aria-valuemax={100}
                aria-label={`Uploading ${item.filename}`}
              >
                <span className="h-full bg-accent" style={{ width: `${item.progress * 100}%` }} />
              </span>
            ) : (
              <span className={cx('text-xs text-fg-subtle', item.status === 'failed' && 'text-danger')}>
                {item.status === 'queued'
                  ? 'Waiting for save'
                  : item.status === 'completing'
                    ? 'Checking…'
                    : item.status === 'done'
                      ? 'Attached'
                      : item.status === 'failed'
                        ? (item.error ?? 'Failed')
                        : 'Preparing…'}
              </span>
            )}
          </div>
        ))}

        <button
          type="button"
          className={cx(
            'flex aspect-[1/0.95] w-full flex-col items-center justify-center gap-2 rounded-md border-2 border-dashed p-2 text-center text-xs font-medium text-accent',
            dragging
              ? 'border-accent bg-accent-soft'
              : 'border-line bg-surface hover:border-accent hover:bg-accent-soft',
          )}
          onClick={fromPicker}
        >
          <Icon name="upload" size="1.5rem" />
          Upload document
        </button>
      </div>
    </div>
  )
}
